import React, { useEffect, useMemo } from 'react';
import { withPermission } from 'hoc';
import { green, red, grey } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { HeadlinePage, SearchBarBox, KpiCard, TableCustom, Alert } from 'components/common';
import { useForm } from 'react-hook-form';
import { useStyle } from './styles';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import NearbyErrorRoundedIcon from '@mui/icons-material/NearbyErrorRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { useState } from 'react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);


const DashboardDumb = ({
  isLoaded,
  handleSearch,
  options,
  defaultValues,
  data,
  handleRedirect,
  handleRunJob,
  validPermission
}) => {
  const methods = useForm({
    defaultValues: useMemo(() => {
      return defaultValues;
    }, [defaultValues])
  });
  const { reset } = methods;
  const projectId = methods.getValues("project");
  const classes = useStyle();
  const [t] = useTranslation();
  const [dateRangeError,setDateRangeError] = useState(false)

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const onSubmit = (data) => {
    if (data.dateFrom > data.dateTo){
      setDateRangeError(true);
    }else{
      handleSearch(data);
    }
  };
  const handleCloseAlert = ()=>{
    setDateRangeError(false);
  }

  const onRunJob = (workflow) => {
    handleRunJob(workflow);
  };

  const kpis = [
    { color: 'blue', label: 'home.components.kpis.projects', value: data.kpi.projects, icon: LayersRoundedIcon },
    { color: 'green', label: 'home.components.kpis.nodes', value: data.kpi.nodes, icon: AccountTreeRoundedIcon },
    { color: 'red', label: 'home.components.kpis.errors', value: data.kpi.errors, icon: NearbyErrorRoundedIcon },
    { color: 'purple', label: 'home.components.kpis.inventories', value: data.kpi.inventories, icon: Inventory2RoundedIcon },
    { color: 'yellow', label: 'home.components.kpis.services', value: data.kpi.services, icon: SettingsRoundedIcon }
  ];

  const dataLineChart = {
    labels: data.jobs.labels,
    datasets: [
      {
        label: t('home.components.chartLine.firstLabel'),
        data: data.jobs.success.values,
        fill: false,
        borderColor: green[500],
        backgroundColor: green[500],
        tension: 0.5,
        borderWidth: 5
      },
      {
        label: t('home.components.chartLine.secondLabel'),
        data: data.jobs.failed.values,
        fill: false,
        borderColor: red[500],
        backgroundColor: red[500],
        tension: 0.5,
        borderWidth: 5
      }
    ]
  };

  const dataPieChart = {
    labels: [t('home.components.chartLine.firstLabel'), t('home.components.chartLine.secondLabel')],
    datasets: [
      {
        data: [data.jobs.success.total, data.jobs.failed.total],
        backgroundColor: [green[500], red[500]],
        borderWidth: 0,
        cutout: '60%'
      },
    ],
  };

  const optionsPieChart = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
  };

  const optionsLineChart = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        rtl: false,
        title: {
          padding: 10
        },
        labels: {
          usePointStyle: true,
          boxWidth: 4,
          boxHeight: 4,
          font: {
            family: 'Montserrat, sans-serif',
            size: 14
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          font: {
            family: 'Montserrat, sans-serif',
          }
        },
        grid: {
          display: false,
          borderWidth: 0,
        }
      },
      x: {
        ticks: {
          font: {
            family: 'Montserrat, sans-serif',
          }
        },
        grid: {
          borderWidth: 0,
          borderDash: [20, 20],
          lineWidth: 2
        }
      }
    }
  };

  const createActions = () => {
    const actions = [];

    if (validPermission(projectId, 'isExecute')) actions.push({
      label: t('workflowList.components.table.run'),
      icon: <PlayCircleOutlineOutlinedIcon color='primary' />,
      handle: onRunJob
    });

    return actions;
  }

  const columns = [
    { label: t('common.components.table.col.name'), key: 'name' },
    { label: t('common.components.table.col.rate'), key: 'successRate', type: 'chip' },
    { label: t('common.components.table.col.jobs'), key: 'jobs', type: 'job' },
  ];

  const actions = createActions();

  return (
    <>
      <HeadlinePage title={'home.headline'}/>

      <Grid container spacing={2}>

        { kpis.map((kpi, index) => (
          <Grid key={index} item xs={12} sm={6} md={4} lg={2.4}>
            <KpiCard
              color={kpi.color}
              label={kpi.label}
              value={kpi.value}
              Icon={kpi.icon}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={12} md={12} lg={12}>
          <SearchBarBox
            onSubmit={onSubmit}
            methods={methods}
            options={options}
            hideSearch={true}
            hideDate={false}
          />
        </Grid>

        {validPermission(projectId, 'isVisible')?
        <>
          <Grid item xs={12} sm={12} md={9} lg={8}>
            <Paper className={classes.paperStyle} sx={{ position: 'relative' }}>
              <Typography variant="h6">
                {t('home.components.chartLine.title')}
              </Typography>
              <Line
                options={optionsLineChart}
                data={dataLineChart}
              />

              { (!data.jobs.success.total && !data.jobs.failed.total) &&
                <Box sx={{ position: 'absolute', right: 16, bottom: 16, color: 'red', gap: 1, display: 'flex', alignItems: 'center' }}>
                  <WarningAmberIcon fontSize='small'/>
                  <Typography variant='caption'>
                    {t('home.components.notResults')}
                  </Typography>
                </Box>
              }
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={3} lg={4} >
            <Paper className={classes.paperStyle} sx={{ height: '100%', position: 'relative' }}>
              <Grid container sx={{ width: '100%', height: '100%'}}>
                <Grid item xs={4} sm={4} md={12} lg={3}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('home.components.pieChart.title')}
                    </Typography>
                    <Typography variant="h3">
                      {data.jobs.success.total + data.jobs.failed.total}
                    </Typography>
                    <Typography variant="body1" sx={{ color: grey[500] }}>
                      {t('home.components.pieChart.subtitle')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={8} sm={4} md={12} lg={9} sx={{ display: 'flex', p: 1, justifyContent: 'center', alignItems: 'center' }}>
                  <Doughnut
                    options={optionsPieChart}
                    data={dataPieChart}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'absolute'}}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='subtitle1' sx={{ color: grey[500] }}>{data.jobs.success.total}</Typography>
                      <KeyboardArrowUpIcon fontSize='large' sx={{ color: green[500] }}/>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='subtitle1' sx={{ color: grey[500] }}>{data.jobs.failed.total}</Typography>
                      <KeyboardArrowUpIcon fontSize='large' sx={{ color: red[500], transform: 'rotateZ(180deg)' }}/>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              { (!data.jobs.success.total && !data.jobs.failed.total) &&
                <Box sx={{ position: 'absolute', right: 16, bottom: 16, color: 'red', gap: 1, display: 'flex', alignItems: 'center' }}>
                  <WarningAmberIcon fontSize='small'/>
                  <Typography variant='caption'>
                    {t('home.components.notResults')}
                  </Typography>
                </Box>
              }
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Paper sx={{ p: 2 }}>
              <TableCustom
                title="workflowList.components.titleList"
                columns={columns}
                content={data.workflows}
                loading={isLoaded}
                handleNotResults={handleRedirect}
                actions={actions}
              />
            </Paper>
          </Grid>
        </>:null}
      </Grid>
      <Alert severity="error" handleClose={handleCloseAlert} open={dateRangeError} message="home.notCorrectDateRange" />
    </>
  )
}

export default withPermission(DashboardDumb, 'dasboard');
